/**
 * Pie or donut: the first measure, one slice per row. A donut carries the
 * total in its hole; the tooltip gives each slice's value and share.
 *
 * The legend is HTML below the SVG, not recharts' <Legend>: with the legend
 * outside, the circle sits at the exact centre of its box, so the donut's
 * total can be an HTML overlay. (recharts 3 drew nothing for a <Label
 * position="center"> with custom content inside <Pie>.)
 *
 * @module components/looker/charts/PieTileChart
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import type { LookerTile } from '@/services/looker/lookerTypes';
import { cellText, chartRows, compactNumber, formatSharePercent, SERIES_COLORS } from '../lookerChartData';
import { TOOLTIP_PROPS, VALUE_LABEL_PROPS, fullNumber } from './chartTheme';

const PIE_HEIGHT = 230;

export const PieTileChart: React.FC<{ tile: LookerTile }> = ({ tile }) => {
  const { t } = useTranslation();
  const { measures, data } = chartRows(tile);
  const m = measures[0];
  if (!m) return null;
  const slices = data
    .filter((d) => typeof d[m.name] === 'number')
    .map((d, i) => ({ x: d.x, value: d[m.name] as number, color: tile.vis.seriesColors[d.x] ?? SERIES_COLORS[i % SERIES_COLORS.length] }));
  const total = slices.reduce((sum, d) => sum + Math.max(0, d.value), 0);
  const share = (v: unknown) => (typeof v === 'number' ? formatSharePercent(v, total) : null);
  const donut = tile.vis.donut;
  const absentNote = tile.absent && tile.absent.length > 0
    ? <p className="mt-2 text-center text-xs text-neutral-700">{t('looker.noSpendIn', { list: tile.absent.join(', ') })}</p>
    : null;
  if (slices.length === 1) {
    // The measure as Looker renders it (currency, decimals), not a compacted guess.
    const only = data.find((d) => typeof d[m.name] === 'number');
    const singleText = (only ? cellText(tile.rows[data.indexOf(only)]?.[m.name]) : null) ?? compactNumber(slices[0].value);
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-neutral-900">{t('looker.singleSlice', { name: slices[0].x })}</p>
        <p className="mt-1 text-xl font-bold tabular-nums text-neutral-900">{singleText}</p>
        <p className="text-xs text-neutral-700">{m.label}</p>
        {absentNote}
      </div>
    );
  }
  return (
    <div>
      <div className="relative" style={{ height: PIE_HEIGHT }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices} dataKey="value" nameKey="x" name={m.label}
              outerRadius={tile.vis.valueLabels ? 80 : 100} innerRadius={donut ? (tile.vis.valueLabels ? 52 : 66) : 0}
              paddingAngle={donut && slices.length > 1 ? 2 : 0}
              stroke="#ffffff" strokeWidth={2} isAnimationActive={false}
              label={tile.vis.valueLabels ? ({ value }: { value?: unknown }) => share(value) ?? '' : false}
              labelLine={tile.vis.valueLabels}
              {...(tile.vis.valueLabels ? { style: VALUE_LABEL_PROPS } : {})}
            >
              {slices.map((s) => <Cell key={s.x} fill={s.color} />)}
            </Pie>
            <Tooltip
              {...TOOLTIP_PROPS}
              formatter={(v: unknown, name: unknown) => [`${fullNumber(v)}${share(v) ? ` (${share(v)})` : ''}`, String(name)]}
            />
          </PieChart>
        </ResponsiveContainer>
        {donut && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold tabular-nums text-neutral-900">{compactNumber(total)}</span>
            <span className="text-xs text-neutral-700">{m.label}</span>
          </div>
        )}
      </div>
      <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-neutral-800">
        {slices.map((s) => (
          <li key={s.x} className="inline-flex items-center gap-1.5">
            <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
            {s.x}
            <span className="tabular-nums text-neutral-700">{share(s.value)}</span>
          </li>
        ))}
      </ul>
      {absentNote}
    </div>
  );
};
